import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Compass, LogOut, Monitor, Moon, Search, Sun } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../features/auth/AuthContext';
import { useTheme } from '../features/theme/ThemeProvider';
import { cn } from '../lib/utils';
import { replayTour } from './GuidedTour';
import { NAV_ITEMS, NAV_ITEMS_SYSTEM, filterNavItems, type NavItem } from './nav';

const OPEN_EVENT = 'minirag:open-command-palette';

/** Opens the palette from anywhere (e.g. the topbar search button) without
 * that caller needing to hold or lift the palette's open-state. */
export function openCommandPalette() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

interface Entry {
  key: string;
  label: string;
  icon: NavItem['icon'];
  run: () => void;
}

/**
 * Global ⌘K / Ctrl+K launcher. Owns its own open state — mounted once in
 * `AppShell`, no props needed. Entries are built from the same `NAV_ITEMS` /
 * `NAV_ITEMS_SYSTEM` + `filterNavItems` the sidebar renders from, so the
 * palette can never offer a page the current role can't actually reach.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { can, logout } = useAuth();
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Lets a visible trigger (the topbar search button) open the palette
  // without lifting its open-state out of this self-contained component —
  // any element anywhere can open it by dispatching this event.
  useEffect(() => {
    function onOpenRequest() {
      setOpen(true);
    }
    window.addEventListener(OPEN_EVENT, onOpenRequest);
    return () => window.removeEventListener(OPEN_EVENT, onOpenRequest);
  }, []);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    // Radix moves focus into the content on open; nudge it onto the input
    // specifically, one tick later so it isn't fought for by that default.
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const nextTheme = theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark';
  const themeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;

  const entries: Entry[] = useMemo(() => {
    const pages = [...filterNavItems(NAV_ITEMS, can), ...filterNavItems(NAV_ITEMS_SYSTEM, can)].map(
      (item): Entry => ({
        key: `nav:${item.to}`,
        label: item.label,
        icon: item.icon,
        run: () => navigate(item.to),
      }),
    );
    const actions: Entry[] = [
      {
        key: 'action:theme',
        label: `Switch to ${nextTheme} theme`,
        icon: themeIcon,
        run: () => setTheme(nextTheme),
      },
      {
        key: 'action:tour',
        label: 'Replay the guided tour',
        icon: Compass,
        run: replayTour,
      },
      {
        key: 'action:logout',
        label: 'Sign out',
        icon: LogOut,
        run: () => void logout(),
      },
    ];
    return [...pages, ...actions];
  }, [can, navigate, logout, setTheme, nextTheme, themeIcon]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => e.label.toLowerCase().includes(q));
  }, [entries, query]);

  function select(entry: Entry | undefined) {
    if (!entry) return;
    entry.run();
    setOpen(false);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      select(filtered[activeIndex]);
    }
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" />
        <DialogPrimitive.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            'fixed top-[14vh] left-1/2 z-50 w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2',
            'rounded-[var(--radius-card)] border border-border bg-surface shadow-2xl shadow-black/30 outline-none',
          )}
        >
          <DialogPrimitive.Title className="sr-only">Search</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Jump to a page or run a quick action
          </DialogPrimitive.Description>

          <div className="relative border-b border-border">
            <Search
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Jump to a page or action…"
              className="h-12 w-full bg-transparent pr-4 pl-11 text-sm outline-none placeholder:text-muted-foreground/70"
              aria-label="Search pages and actions"
              role="combobox"
              aria-expanded={open}
              aria-controls="command-palette-list"
              aria-activedescendant={
                filtered[activeIndex] ? `cmd-${filtered[activeIndex].key}` : undefined
              }
            />
          </div>

          <div id="command-palette-list" role="listbox" className="max-h-80 overflow-y-auto p-2">
            {filtered.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                Nothing matches “{query}”.
              </p>
            )}
            {filtered.map((entry, i) => {
              const Icon = entry.icon;
              // Group by key prefix ('nav:' / 'action:') rather than a fixed
              // index — the boundary between groups shifts once the query
              // filters either group down, sometimes to zero.
              const group = entry.key.split(':')[0];
              const isGroupStart = i === 0 || filtered[i - 1]!.key.split(':')[0] !== group;
              return (
                <div key={entry.key}>
                  {isGroupStart && (
                    <p className={cn('label-micro px-3 pb-1.5', i === 0 ? 'pt-1' : 'pt-3')}>
                      {group === 'nav' ? 'Pages' : 'Actions'}
                    </p>
                  )}
                  <button
                    id={`cmd-${entry.key}`}
                    type="button"
                    role="option"
                    aria-selected={i === activeIndex}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => select(entry)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-[var(--radius-control)] px-3 py-2 text-left text-sm transition-colors',
                      i === activeIndex
                        ? 'bg-primary-muted text-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <Icon className="size-4 shrink-0" aria-hidden />
                    <span className="flex-1">{entry.label}</span>
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-3 border-t border-border px-3 py-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-border-strong px-1 font-mono">↑↓</kbd> navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-border-strong px-1 font-mono">↵</kbd> select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-border-strong px-1 font-mono">esc</kbd> close
            </span>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
